import { useParams, useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { useEffect, useState } from 'react';
import { TextField, PrimaryButton, Stack, Spinner, SpinnerSize, DatePicker, Dropdown, Toggle, Label, Separator } from '@fluentui/react';
import { updateItem } from '../../services/graph-service';
import { fetchItemById, getTokenAndListInfo, japaneseDateConverter, phoneNumFormat } from '../../utils/item-helper';
import { japaneseStrings, listname } from '../../constants/global-values';
import { common, formatMessage } from "../../messages/common-items";
import { personalInformation } from '../../messages/personal-Information';
import { CustomDialog } from '../../components/custom-dialog';
import React from 'react';
import FileUpload from '../../components/file-upload';
import { BLOOD_TYPE_OPTIONS, GENDER_OPTIONS, JOBTYPE_OPTIONS } from '../../constants/options';

export default function AdminEditViewPage() {
  const { id } = useParams();
  const { instance } = useMsal();
  const navigate = useNavigate();
  const [formData, setFormData] = useState<any>(null);
  const [token, setToken] = useState("");
  const [loading, setLoading] = useState(false);
  const [siteId, setSiteId] = useState("");
  const [listId, setListId] = useState("");
  const { Labels: Lbl_Common, SuccessMessages: Success_Common, ErrorMessages: Err_Common} = common;
  const {Labels: Lbl_Personal_Info, SuccessMessages: Success_Personal_Info} = personalInformation;
  type phoneNumberField = "PhoneNumber" | "EmergencyPhone";
  const [phoneNumber, setPhoneNumber] = useState<Record<phoneNumberField, string>>({
    PhoneNumber: "",
    EmergencyPhone: ""
  });
  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      try {
        const { token, siteId, listId } = await getTokenAndListInfo(instance, listname);
        setSiteId(siteId);
        setListId(listId);
        setToken(token)
        const found = await fetchItemById(token, siteId, listId, id!);
        setFormData(found);
      }
      catch (error) {
        console.error("Error loading item", error);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [id, instance]);

  const handleChange = (key: string, value: any) => {
    setFormData({ ...formData, [key]: value ?? "" });
  };
  const [hideDialog, setHideDialog] = useState(false);
  const [updatedId, updatedInfoId] = useState();

  const handleSave = async () => {
    await updateItem(token, siteId, listId, formData.id, formData);
    setHideDialog(true);
    updatedInfoId(formData.id);
  };
  const closeDialog = () => {
    setHideDialog(false);
    navigate(`/detail/${updatedId}`);
  }

  const handlePhoneNumberChange = (
    field: 'PhoneNumber' | 'EmergencyPhone',
    event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const input = event.currentTarget.value;
    const digits = input.replace(/\D/g, '').slice(0, 11);
    let formattedPhoneNum = phoneNumFormat(digits);
    setPhoneNumber(prev => ({
      ...prev,
      [field]: formattedPhoneNum,
    }));
    const numFormat = formattedPhoneNum.replace(/-/g, "");
    handleChange(field, numFormat);
  };

  if (loading || !formData) return <div className="loadercommon-container">
    <Spinner label={Success_Common.LOADER_MSG} size={SpinnerSize.large} />
  </div>;
  const SectionHeader: React.FC<{ title: string }> = ({ title }) => (
    <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 10 }}>
      <Label styles={{ root: { whiteSpace: "nowrap", marginLeft: 15 } }}>{title}</Label>
      <Separator styles={{ root: { flexGrow: 1 } }} />
    </Stack>
  );

  return (
    <div className="background list-background">
      <div className="form-container list-box">
        <div >
          <h2>{Lbl_Personal_Info.EDIT}</h2>
          <Stack tokens={{ childrenGap: 10 }}>
            <SectionHeader title={Lbl_Personal_Info.BASIC_INFO_LBL} />
            <TextField
              label={Lbl_Personal_Info.NAME}
              placeholder={formatMessage(Err_Common.REQUIRED_VALUE, Lbl_Personal_Info.NAME)}
              value={formData.Name}
              onChange={(_, val) => handleChange("Name", val)}
              onRenderLabel={() => (
                <div style={{ marginBottom: 4 }}>
                  <label htmlFor="Name" className="custom-label">
                    {Lbl_Personal_Info.NAME}
                    {(<span className="required-label"> {Lbl_Common.REQUIRED} </span>)}
                  </label>
                </div>
              )} />
            <DatePicker
              label={Lbl_Personal_Info.DATEOFBIRTH}
              placeholder={formatMessage(Err_Common.REQUIRED_SELECT, Lbl_Personal_Info.DATEOFBIRTH)}
              strings={japaneseStrings}
              value={formData.DateOfBirth ? new Date(formData.DateOfBirth) : undefined}
              onSelectDate={(date) => {
                handleChange("DateOfBirth", date);
              }}
              formatDate={japaneseDateConverter}
            />
            <Dropdown
              label={Lbl_Personal_Info.GENDER}
              placeholder={formatMessage(Err_Common.REQUIRED_SELECT, Lbl_Personal_Info.GENDER)}
              selectedKey={formData.Gender}
              options={GENDER_OPTIONS}
              onChange={(_, o) => handleChange("Gender", o?.key)} />
            <TextField
              label={Lbl_Personal_Info.ADDRESS}
              placeholder={formatMessage(Err_Common.REQUIRED_VALUE, Lbl_Personal_Info.ADDRESS)}
              value={formData.Address}
              onChange={(_, val) => handleChange("Address", val)} />
            <TextField
              placeholder={formatMessage(Err_Common.REQUIRED_VALUE, Lbl_Personal_Info.PHONENUMBER)}
              id="PhoneNumber"
              type="tel"
              value={formData.PhoneNumber ? phoneNumFormat(formData.PhoneNumber) : ""}
              onChange={(e) => handlePhoneNumberChange("PhoneNumber", e)}
              maxLength={13}
              autoComplete="tel"
              onRenderLabel={() => (
                <div style={{ marginBottom: 4 }}>
                  <label htmlFor="Name" className="custom-label">
                    {Lbl_Personal_Info.PHONENUMBER}
                    {(<span className="required-label"> {Lbl_Common.REQUIRED} </span>)}
                  </label>
                </div>
              )} />
            <SectionHeader title={Lbl_Personal_Info.EMERGENCY_PHONENUMBER_LBL} />
            <TextField
              label={Lbl_Personal_Info.EMERGENCY_PHONENUMBER_LBL}
              placeholder={formatMessage(Err_Common.REQUIRED_VALUE, Lbl_Personal_Info.EMERGENCY_PHONENUMBER_LBL)}
              id="EmergencyPhone"
              type="tel"
              value={formData.EmergencyPhone ? phoneNumFormat(formData.EmergencyPhone) : ""}
              onChange={(e) => handlePhoneNumberChange("EmergencyPhone", e)}
              maxLength={13}
              autoComplete="tel" />
            <TextField
              label={Lbl_Personal_Info.EMERGENCY_NAME}
              placeholder={formatMessage(Err_Common.REQUIRED_VALUE, Lbl_Personal_Info.EMERGENCY_NAME)}
              value={formData.EmergencyName}
              onChange={(_, val) => handleChange("EmergencyName", val)} />
            <Toggle
              label={Lbl_Personal_Info.IS_SOLE_PROPRIETOR}
              checked={formData.IsSoleProprietor === "はい"}
              onChange={(_, checked) =>
                handleChange("IsSoleProprietor", checked ? "はい" : "いいえ")
              } />
            <Toggle
              label={Lbl_Personal_Info.SPECIAL_LABOR_INSURANCE}
              checked={formData.SpecialLaborInsurance === "はい"}
              onChange={(_, checked) =>
                handleChange("SpecialLaborInsurance", checked ? "はい" : "いいえ")
              } />
            <SectionHeader title={Lbl_Personal_Info.HEALTH_INFO_LBL} />
            <Dropdown
              label={Lbl_Personal_Info.BLOOD_TYPE}
              placeholder={formatMessage(Err_Common.REQUIRED_SELECT, Lbl_Personal_Info.BLOOD_TYPE)}
              selectedKey={formData.BloodType}
              options={BLOOD_TYPE_OPTIONS}
              onChange={(_, o) => handleChange("BloodType", o?.key)} />
            <DatePicker
              label={Lbl_Personal_Info.HEALTH_CHECK_DATE}
              placeholder={formatMessage(Err_Common.REQUIRED_SELECT, Lbl_Personal_Info.HEALTH_CHECK_DATE)}
              strings={japaneseStrings}
              value={formData.HealthCheckDate ? new Date(formData.HealthCheckDate) : undefined}
              onSelectDate={(date) => {
                handleChange("HealthCheckDate", date);
              }}
              formatDate={japaneseDateConverter} />
            <SectionHeader title={Lbl_Personal_Info.OCCUPATION_INFO_LBL} />
            <Dropdown
              label={Lbl_Personal_Info.JOB_TYPE}
              placeholder={formatMessage(Err_Common.REQUIRED_SELECT, Lbl_Personal_Info.JOB_TYPE)}
              selectedKey={formData.JobType}
              options={JOBTYPE_OPTIONS}
              onChange={(_, o) => handleChange("JobType", o?.key)} />
            <DatePicker
              label={Lbl_Personal_Info.QUALIFICATION_EXPIRY}
              placeholder={formatMessage(Err_Common.REQUIRED_SELECT, Lbl_Personal_Info.QUALIFICATION_EXPIRY)}
              strings={japaneseStrings}
              value={formData.QualificationExpiry ? new Date(formData.QualificationExpiry) : undefined}
              onSelectDate={(date) => {
                handleChange("QualificationExpiry", date);
              }}
              formatDate={japaneseDateConverter} />
            <FileUpload
              label={Lbl_Personal_Info.QUALIFICATION_PHOTO_LBL}
              placeholder={Success_Personal_Info.NO_IMAGE_SELECTED} />
            <FileUpload
              label={Lbl_Personal_Info.FACE_PHOTOS_LBL}
              placeholder={Success_Personal_Info.NO_IMAGE_SELECTED} />
            <CustomDialog
              hidden={!hideDialog}
              title={Lbl_Common.SAVE}
              message={Success_Personal_Info.PERSONAL_INFO_UPDATED_MSG}
              onClose={closeDialog}
            />
            <Stack horizontal className="buttonallign" tokens={{ childrenGap: 10 }}>
              <PrimaryButton className="custom-primary-button" text={Lbl_Common.SAVE} onClick={handleSave} />
              <PrimaryButton className="cancel-button" text={Lbl_Common.CANCEL} onClick={() => navigate(-1)} />
            </Stack>
          </Stack>
        </div>
      </div>
    </div>
  );
}